import * as React from "react"
import { FaUser } from "react-icons/fa"
import { gql } from "@apollo/client"
import {
  Avatar,
  Box,
  Button,
  Center,
  HStack,
  LinkBox,
  LinkOverlay,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react"

import { GetUserDocument, UserConnectionFragment, useRemoveConnectionMutation } from "lib/graphql"
import { useMutationHandler } from "lib/hooks/useMutationHandler"

import { useMe } from "../lib/hooks/useMe"
import { NoData } from "./NoData"

const _ = gql`
  fragment UserConnection on UserConnection {
    id
    user {
      id
      fullName
      avatarUrl
      interests
    }
    follower {
      id
      fullName
      avatarUrl
      interests
    }
  }
`

interface Props {
  userId: string
  connections: UserConnectionFragment[]
}

export const ConnectionsModalContent = ({ connections, userId }: Props) => {
  return (
    <Box>
      <Tabs>
        <TabList pt={2} pb={2}>
          <Tab
            color="gray.500"
            _selected={{ color: "white", borderColor: "pink.500" }}
            p={2}
            fontWeight="bold"
          >
            All connections
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel p={0} pt={5}>
            {connections.length === 0 ? (
              <Center h="100px">
                <NoData>No connections yet</NoData>
              </Center>
            ) : (
              connections?.map((connection) => (
                <ConnectionItem key={connection.id} connection={connection} userId={userId} />
              ))
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  )
}

interface ConnectionItemProps {
  userId: string
  connection: UserConnectionFragment
}

const ConnectionItem = ({ connection, userId }: ConnectionItemProps) => {
  const follower = connection.follower.id === userId ? connection.user : connection.follower
  const { me } = useMe()
  const handler = useMutationHandler()

  const [removeConnection, { loading }] = useRemoveConnectionMutation()

  const handleRemoveConnection = () => {
    return handler(
      () =>
        removeConnection({
          variables: { userId: follower.id },
          refetchQueries: [
            { query: GetUserDocument, variables: { id: follower.id } },
            { query: GetUserDocument, variables: { id: me?.id || "" } },
          ],
        }),
      {
        onSuccess: (_, toast) => {
          toast({ description: "Connectie verwijderd" })
        },
      },
    )
  }
  return (
    <HStack marginBottom={5}>
      <LinkBox display="flex" flexDir="row" alignItems="center" marginRight="auto">
        <Avatar
          borderColor="pink.500"
          borderWidth={3}
          w="70px"
          h="70px"
          mr={5}
          bg="gray.600"
          icon={<FaUser color="gray.900" size={22} />}
          src={follower.avatarUrl || undefined}
          name={follower.fullName || "avatar"}
        />
        <Stack spacing={0}>
          <LinkOverlay href={`/users/${follower?.id}`} textDecoration="none" fontWeight="black" fontSize="xl">
            {follower.fullName}
          </LinkOverlay>
          <Text mt={0} pt={0} color="pink.500" fontSize="sm" fontWeight="semibold">
            {follower?.interests.map(
              (interest: string, index) =>
                `${interest}${follower?.interests.length - 1 !== index ? " · " : ""}`,
            )}
          </Text>
        </Stack>
      </LinkBox>
      {me && (connection.user.id === me.id || connection.follower.id === me.id) && (
        <Button
          colorScheme="gray"
          isLoading={loading}
          isDisabled={loading}
          variant="outline"
          size="xs"
          onClick={handleRemoveConnection}
        >
          Remove
        </Button>
      )}
    </HStack>
  )
}
